import { BuildSourceCard } from './BuildSourceCard'
import { buildErrors } from '../../core/imageBuild'
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Lock } from 'lucide-react'

import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { Card } from '../../ui/Card'
import { Dialog, DialogContent } from '../../ui/Dialog'
import { PageHeader } from '../shared/PageHeader'
import { RentalChoiceStrip } from './RentalChoiceStrip'
import { SizeEnvelope } from './SizeEnvelope'
import { WorkloadCard } from './WorkloadCard'
import { ConfigSummary, summaryPrice } from './ConfigSummary'
import { SummaryBar } from './SummaryBar'
import { SizeContext, type SizeContextState } from './SizeContext'
import { ResourceControl, meterIsReadable, type ResourceMeter } from './ResourceControl'
import { EnvList, EnvPasteButton } from './EnvList'
import { PortList, portCountLabel } from './PortList'
import { VolumeSection } from './VolumeSection'
import { S3Connection, type S3InstancesState } from './S3Connection'
import { DEFAULT_GUIDED_DRAFT, draftStorageKey, guidedDraftToSdl, maxBodyError, readStoredDraft, rentalDraftToSdl, restrictedPorts, servesHttpIngress, volumeErrors, type GuidedDraft } from '../../core/guided'
import { normalizeAllowFrom } from '../../core/allowFrom'
import { NO_MY_IP, type MyIpState } from './PortAccess'
import { writeDraftOrigin } from '../../core/draftOrigin'
import { duplicateKeyIndexes } from '../../core/envFile'
import { RENTAL_PATH_COPY, isDirect, isHostProduct, resolveMode, volumeUnavailableReason, type RentalPath } from '../../core/rental'
import { missingRequiredEnv, plaintextConfirmError, popularImageForRepository, unacknowledgedPlaintext } from '../../core/popularImages'
import { reapplyS3Mapping, type S3InstanceCandidate } from '../../core/s3Connection'
import { fieldIdForError, readiness } from '../../core/readiness'
import { priceForDraft, unitRates } from '../../core/rateCard'
import { BLOCK_SECONDS, formatUsdPerHour, usdDdtPerHour } from '../../core/pricing'
import { DEFAULT_SIZE_BOUNDS, GIB, boundFor, clampDraftResources, feasibleCount, feasibleHostCount, limitingAxes, normalizedReplicaCount, poolSizeBounds, requestedCapacityForDraft, totalStorageGi, type CapacityEnvelope, type SizeBounds, type SliderBound } from '../../core/capacityEnvelope'
import { FIXED_PRESET_IDS, SIZE_AXES, SIZE_PRESETS, clampSliderValue, defaultPreset, derivePreset, formatCores, isFixedPreset, matchesPreset, nearLimitAxes, presetFits, shortfallText, type FixedPresetId, type SizeAxis, type SizePresetId, type SizeValues } from '../../core/sizing'
import type { SizeCardModel } from './SizePicker'
import type { CatalogCapacityDto, CatalogHostDto, RentalCapacityDto, RentalHostOfferDto } from '../../data/types'

type CapacitySnapshot = {
  cpuMilli: number | null
  memoryBytes: number | null
  storageBytes: number | null
  gpuUnits: number | null
}

function capacityNumber(value: number | string | null | undefined): number | null {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : null
  return parsed !== null && Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

function capacitySnapshot(capacity: CatalogCapacityDto | RentalCapacityDto | null | undefined): CapacitySnapshot {
  if (!capacity) return { cpuMilli: null, memoryBytes: null, storageBytes: null, gpuUnits: null }
  const value = capacity as CatalogCapacityDto & Partial<RentalCapacityDto>
  return {
    cpuMilli: capacityNumber(value.cpuMilli ?? value.cpu_millicores),
    memoryBytes: capacityNumber(value.memoryBytes ?? value.memory_bytes),
    storageBytes: capacityNumber(value.storageBytes ?? value.storage_bytes),
    gpuUnits: capacityNumber(value.gpuUnits ?? value.gpu_units),
  }
}

function initialSliderValue(offered: number | null, available: number | null, bound: SliderBound, divisor = 1): number {
  if (offered === null) return clampSliderValue(bound.min, bound)
  const offeredValue = offered / divisor
  const availableValue = available === null ? offeredValue : Math.min(offeredValue, available / divisor)
  return clampSliderValue(availableValue, bound)
}

function sizeConfigFor(host: CatalogHostDto | null, offer: RentalHostOfferDto | null): { bounds: SizeBounds; starting: Pick<GuidedDraft, 'cpu' | 'ram' | 'storage'>; available: CapacitySnapshot; inventory: CapacitySnapshot } | null {
  // Rental offers are the provider's direct-rental contract: allocatable is
  // what the provider offers, while available is the free capacity right now.
  // The catalog snapshot remains a useful fallback when a direct provider
  // offer has not arrived yet; competitive requests never use it for sizing.
  const rawInventory = offer ? offer.allocatable : host?.observation_state === 'fresh' ? host.inventory : null
  const rawAvailable = offer ? offer.available : host?.observation_state === 'fresh' ? host.available : null
  if (!rawInventory || !rawAvailable) return null
  const inventory = capacitySnapshot(rawInventory)
  const available = capacitySnapshot(rawAvailable)
  if ([
    inventory.cpuMilli, inventory.memoryBytes, inventory.storageBytes,
    available.cpuMilli, available.memoryBytes, available.storageBytes,
  ].some((value) => value === null)) return null

  const bounds: SizeBounds = {
    cpu: boundFor(available.cpuMilli, DEFAULT_SIZE_BOUNDS.cpu),
    ram: boundFor(available.memoryBytes === null ? null : Math.floor(available.memoryBytes / GIB), DEFAULT_SIZE_BOUNDS.ram),
    storage: boundFor(available.storageBytes === null ? null : Math.floor(available.storageBytes / GIB), DEFAULT_SIZE_BOUNDS.storage),
  }
  return {
    bounds,
    starting: {
      cpu: initialSliderValue(inventory.cpuMilli, available.cpuMilli, bounds.cpu),
      ram: initialSliderValue(inventory.memoryBytes, available.memoryBytes, bounds.ram, GIB),
      storage: initialSliderValue(inventory.storageBytes, available.storageBytes, bounds.storage, GIB),
    },
    available,
    inventory,
  }
}

/**
 * How a host-bound request sits in that host, in draft units. `free` is the
 * slider bound (already floored to the step) so the meter and the control can
 * never disagree about how much is left. `used` is taken from the raw reading
 * instead: deriving it as total − free would count the step-floor remainder
 * (2900m free of 4000m → 1.25 cores) as in use by others.
 */
function sizeMetersFor(config: { bounds: SizeBounds; inventory: CapacitySnapshot; available: CapacitySnapshot }, draft: Pick<GuidedDraft, 'cpu' | 'ram' | 'storage'>): Record<SizeAxis, ResourceMeter | undefined> {
  const meter = (requested: number, free: number, inventory: number | null, available: number | null, divisor = 1): ResourceMeter | undefined => {
    // More free than the host has is an inconsistent reading, not a meter.
    if (inventory === null || available === null || available > inventory) return undefined
    const candidate = { requested, free, used: (inventory - available) / divisor, total: Math.floor(inventory / divisor) }
    return meterIsReadable(candidate) ? candidate : undefined
  }
  return {
    cpu: meter(draft.cpu, config.bounds.cpu.max, config.inventory.cpuMilli, config.available.cpuMilli),
    ram: meter(draft.ram, config.bounds.ram.max, config.inventory.memoryBytes, config.available.memoryBytes, GIB),
    storage: meter(draft.storage, config.bounds.storage.max, config.inventory.storageBytes, config.available.storageBytes, GIB),
  }
}

/**
 * Why a host product's size is fixed. The provider prices these products on
 * the host, not on the request (`provider/rental/ratecard.go`
 * `PriceForProduct`): the shared product on everything free now, the exclusive
 * one on the whole machine. Lowering the sliders would shrink what the tenant
 * gets without shrinking what they pay, so they are not offered.
 */
/**
 * Why a host product has no replicas. Each replica would need the whole host
 * (the provider's quote asks the node for count × its free capacity and
 * refuses), and the provider prices the product once, not per replica.
 */
const REPLICAS_ON_HOST_PRODUCT = 'Replicas need a host each, and this rents one whole host. Choose custom capacity to run replicas.'

const HOST_PRODUCT_SIZE_NOTE: Partial<Record<string, string>> = {
  RENTAL_MODE_DIRECT_HOST_EXCLUSIVE: 'You rent the whole machine, and the host prices it on all of it, so the size is fixed.',
  RENTAL_MODE_DIRECT_HOST_AVAILABLE: 'You rent all free capacity on this host at a fixed price. Sizes can\'t be changed. Pick another size to pay only for what you use.',
}

const SIZED_NOTE = 'You pay for the size you pick. Drag a bar or type a value to fine-tune; the size switches to Custom.'

const CARD_LABEL: Record<SizePresetId, string> = { small: 'Small', medium: 'Medium', large: 'Large', whole: 'Whole host', custom: 'Custom' }

/** True when every axis has something free to rent. */
function hasRoom(config: { bounds: SizeBounds } | null): boolean {
  return Boolean(config && SIZE_AXES.every((axis) => config.bounds[axis].max > 0))
}

/**
 * The choice a draft holds, read against its values. A preset whose values
 * were changed since (an app's suggested size, a drag) is Custom now; a draft
 * saved before the choice was kept derives one, but only from a size the
 * tenant set — a host product's seeded size was never theirs.
 */
function draftChoice(draft: GuidedDraft): SizePresetId | undefined {
  const choice = draft.sizePreset
  if (choice === 'whole' || choice === 'custom') return choice
  if (isFixedPreset(choice)) return matchesPreset(draft, SIZE_PRESETS[choice]) ? choice : 'custom'
  return draft.sizedAxes?.length === SIZE_AXES.length ? derivePreset(draft) : undefined
}

/**
 * Writes a size choice into the draft. `whole` only records the choice: the
 * host product's size follows the host's offer (see the seeding effect).
 * Custom keeps the values in hand, or starts from Small (clamped) when the
 * whole host's were in hand, so leaving the whole host never leaves its size
 * behind as a custom request.
 */
function applySizeChoice(current: GuidedDraft, id: SizePresetId | null, bounds: SizeBounds): GuidedDraft {
  if (id === null) return current
  if (id === 'whole') return { ...current, sizePreset: 'whole', sizedAxes: undefined }
  const allAxes = [...SIZE_AXES]
  if (id === 'custom') {
    const from: SizeValues = current.sizePreset === 'whole' || draftChoice(current) === undefined ? SIZE_PRESETS.small : current
    return { ...clampDraftResources({ ...current, cpu: from.cpu, ram: from.ram, storage: from.storage }, bounds), sizePreset: 'custom', sizedAxes: allAxes }
  }
  const { cpu, ram, storage } = SIZE_PRESETS[id]
  return { ...clampDraftResources({ ...current, cpu, ram, storage }, bounds), sizePreset: id, sizedAxes: allAxes }
}

function summaryLine(size: SizeValues): string {
  return `${formatCores(size.cpu)} · ${size.ram.toLocaleString('en-US')} GB memory · ${size.storage.toLocaleString('en-US')} GB storage`
}

const AXIS_NAME: Record<SizeAxis, string> = { cpu: 'CPU', ram: 'memory', storage: 'storage' }

/**
 * Precedence between the link a tenant just followed and the draft they left
 * behind (decision D9).
 *
 * **The URL wins.** The query string is what the tenant did seconds ago on the
 * path chooser; a stored draft is whatever they abandoned last time. When the
 * two disagree about the price cap, silently preferring the draft would charge
 * a ceiling they did not just type — the failure `core/rental` exists to make
 * unrepresentable, arriving through the back door of stale state instead of
 * through a mislabelled control. Everything the link does NOT carry (name,
 * image, ports, environment, sizing) is still restored from the draft, so the
 * policy costs a returning tenant nothing they did not overrule themselves.
 *
 * `max` is read as the whole story for a direct path, empty included: arriving
 * on a direct product with no `max` means "no tenant maximum", and letting a
 * stored cap survive there would flip the product to competitive. On a
 * competitive or mode-less link an absent `max` is the open-request marker;
 * stale saved caps are cleared rather than silently resurrected.
 */
function applyLinkPrecedence(
  stored: GuidedDraft,
  { rentalPath, tenantMax }: { rentalPath: RentalPath | undefined; tenantMax: string | undefined },
): GuidedDraft {
  const linkCap = tenantMax?.trim() ?? ''
  if (rentalPath !== undefined && rentalPath !== 'competitive') {
    return {
      ...stored,
      priceCap: linkCap,
    }
  }
  // Competitive requests are open by default. Preserve a non-empty maximum
  // from an old deep link, but never resurrect a stale saved cap.
  return { ...stored, priceCap: linkCap }
}

/**
 * Custom capacity from the same provider. The link names only the provider:
 * a host offer never advertises custom capacity, and the provider places a
 * custom quote on the first node with room rather than on a named one.
 */
function customCapacityHref(owner: string): string {
  const params = new URLSearchParams({ host: owner, path: 'custom-direct' })
  return `/app/rent/configure?${params.toString()}`
}

const SizePicker = lazy(() => import('./SizePicker').then((module) => ({ default: module.SizePicker })))
const AvailabilityOptions = lazy(() => import('./AvailabilityOptions').then((module) => ({ default: module.AvailabilityOptions })))

/**
 * `postedRate` is the selected host offer's per-block price for the chosen
 * host product, computed in the app layer (features do not fetch): a string
 * when the host posts one, null when its offer posts none for this product,
 * and undefined when there is no offer to read or the mode is not a host
 * product.
 */
/** Without a wallet there are no instances of the tenant's own to offer. */
const S3_INSTANCES_UNAVAILABLE: S3InstancesState = { status: 'unavailable', reason: 'Connect a wallet to pick one of your S3 instances.' }

export function ConfigurePage({ walletAddress, selectedHost = null, selectedOffer = null, hostLoading = false, rentalPath, tenantMax, capacityEnvelope = null, capacityEnvelopeLoading = false, capacityEnvelopeError, postedRate, onNavigate, myIp = NO_MY_IP, s3Instances = S3_INSTANCES_UNAVAILABLE, resolveS3Endpoint }: { walletAddress?: string | undefined; selectedHost?: CatalogHostDto | null; selectedOffer?: RentalHostOfferDto | null; hostLoading?: boolean; rentalPath?: RentalPath | undefined; tenantMax?: string | undefined; capacityEnvelope?: CapacityEnvelope | null; capacityEnvelopeLoading?: boolean; capacityEnvelopeError?: unknown; postedRate?: string | null | undefined; onNavigate?: ((path: string) => void) | undefined; onRefreshHost?: (() => void | Promise<unknown>) | undefined; myIp?: MyIpState ; s3Instances?: S3InstancesState | undefined; resolveS3Endpoint?: ((candidate: S3InstanceCandidate) => Promise<string | null>) | undefined }) {
  const storageKey = draftStorageKey(walletAddress)
  /** The maximum the tenant typed on the chooser, as this link carries it. */
  const linkCap = tenantMax?.trim() ?? ''
  const storedDraftAtMount = useMemo(() => readStoredDraft(walletAddress), [walletAddress])
  const [draft, setDraft] = useState<GuidedDraft>(() => applyLinkPrecedence(storedDraftAtMount ?? DEFAULT_GUIDED_DRAFT, { rentalPath, tenantMax }))
  const [restored, setRestored] = useState(() => Boolean(storedDraftAtMount))
  const [submitted, setSubmitted] = useState(false)
  const [sdlConfirmOpen, setSdlConfirmOpen] = useState(false)
  const [sdlError, setSdlError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState(false)
  const [envPasteOpen, setEnvPasteOpen] = useState(false)

  // The product the tenant chose on the path chooser, and the mode that
  // choice plus their price actually resolves to. `resolveMode` is asked
  // rather than trusting `rentalPath`, because a tenant who types a maximum
  // here has changed the product — the same rule the chooser announced, still
  // true one screen later.
  //
  // On a host, the Size card picks the product too (decision 2026-10-06): the
  // whole host is the shared-capacity host product, priced by the host on all
  // of it; every other size is custom capacity from the same host, priced on
  // exactly that size (provider `rental/quote.go` `priceBasis`). So both host
  // links open the same card, and the card's choice is the path.
  const hostSizing = Boolean(selectedHost) && (rentalPath === 'host-available' || rentalPath === 'custom-direct') && linkCap === ''
  const effectivePath: RentalPath | undefined = hostSizing ? (draft.sizePreset === 'whole' ? 'host-available' : 'custom-direct') : rentalPath
  const rentalMode = effectivePath ? resolveMode({ path: effectivePath, priceCap: draft.priceCap }) : undefined
  const chosenPath = effectivePath ? RENTAL_PATH_COPY[effectivePath] : undefined
  const directRental = rentalMode !== undefined && rentalMode !== 'RENTAL_MODE_COMPETITIVE'
  const openRequest =
    (rentalMode === undefined || rentalMode === 'RENTAL_MODE_COMPETITIVE') && draft.priceCap.trim() === ''
  // Only a host-bound direct mode may use a host offer or host snapshot for
  // sizing. A competitive request can arrive with `?host=` as a starting
  // point, but that host is not a binding and must not cap the network-wide
  // request. Mode-less legacy links also stay unbounded because their eventual
  // competitive request is not tied to the host in the URL.
  // Custom capacity is quoted against the provider, not a host offer, so only
  // a host product is sized (and priced) on the offer.
  const sizingOffer = rentalMode !== undefined && isHostProduct(rentalMode) ? selectedOffer : null
  const sizingHost = rentalMode !== undefined && isDirect(rentalMode) ? selectedHost : null
  const sizeConfig = useMemo(() => sizeConfigFor(sizingHost, sizingOffer), [sizingHost, sizingOffer])
  // Both sides of a host's Size card, whichever is chosen now.
  // The whole host is the shared-capacity product, so only an offer that sells it.
  const wholeOffer = hostSizing && selectedOffer?.products.includes('RENTAL_MODE_DIRECT_HOST_AVAILABLE') ? selectedOffer : null
  const wholeConfig = useMemo(() => wholeOffer ? sizeConfigFor(selectedHost, wholeOffer) : null, [selectedHost, wholeOffer])
  const customConfig = useMemo(() => hostSizing ? sizeConfigFor(selectedHost, null) : null, [hostSizing, selectedHost])
  // Only use a current provider observation to construct a direct custom
  // ceiling. A stale rate card cannot authorize the price carried into a quote.
  const hostRates = useMemo(
    () => unitRates(sizingHost?.observation_state === 'fresh' ? sizingHost.unit_prices : null),
    [sizingHost?.observation_state, sizingHost?.unit_prices],
  )
  // What each side of the card can offer on this host right now (decision D3):
  // a size the tenant picks needs the host's current rates; the whole host
  // needs its offer and a posted price for it.
  const customAvailable = hostSizing && hasRoom(customConfig) && hostRates !== null
  const wholeAvailable = hostSizing && hasRoom(wholeConfig) && postedRate !== null
  const customUnavailable = !hostSizing || customAvailable ? null
    : !customConfig ? 'This host has not reported its free capacity.'
    : !hasRoom(customConfig) ? 'This host has no free capacity right now.'
    : 'This host publishes no current rates for a size you pick, so only the whole host can be rented.'
  const wholeUnavailable = !hostSizing || wholeAvailable ? null
    : !wholeOffer || !wholeConfig ? 'This host is not offering its free capacity as a whole right now.'
    : !hasRoom(wholeConfig) ? 'This host has no free capacity right now.'
    : 'This host posts no price for its whole free capacity.'
  const customBounds = customConfig?.bounds ?? null
  const startingChoice = defaultPreset(customBounds, { customAvailable, wholeAvailable })
  const [sizeNotice, setSizeNotice] = useState<string | null>(null)
  const competitiveRequest = rentalMode === 'RENTAL_MODE_COMPETITIVE'
  const hostProduct = rentalMode !== undefined && isHostProduct(rentalMode)
  const volumeUnavailable = volumeUnavailableReason(rentalMode)
  const envelopeBounds = useMemo(() => competitiveRequest && !capacityEnvelopeLoading && !capacityEnvelopeError ? poolSizeBounds(capacityEnvelope) : null, [capacityEnvelope, capacityEnvelopeError, capacityEnvelopeLoading, competitiveRequest])
  const sizeBounds = sizeConfig?.bounds ?? envelopeBounds ?? DEFAULT_SIZE_BOUNDS
  const sizeUnavailable = Boolean(sizeConfig && [sizeConfig.bounds.cpu.max, sizeConfig.bounds.ram.max, sizeConfig.bounds.storage.max].some((max) => max === 0))
  const envelopeRequested = requestedCapacityForDraft(draft)
  const envelopeReplicas = normalizedReplicaCount(draft)
  // Custom capacity starts with nothing chosen (GuidedDraft.sizedAxes): no
  // price, fit or quote is worked out for a size the tenant has not set.
  const unsizedAxes = useMemo(() => rentalMode === 'RENTAL_MODE_DIRECT_CUSTOM' ? SIZE_AXES.filter((axis) => !draft.sizedAxes?.includes(axis)) : [], [rentalMode, draft.sizedAxes])
  const sized = unsizedAxes.length === 0
  const customCapacityPrice = rentalMode === 'RENTAL_MODE_DIRECT_CUSTOM' && hostRates && sized
    ? priceForDraft(hostRates, draft, envelopeReplicas)
    : null
  const providerPricePerHour = customCapacityPrice && customCapacityPrice.totalPerBlock > 0n
    ? usdDdtPerHour({ denom: 'uddt', amount: customCapacityPrice.totalPerBlock.toString() }, BLOCK_SECONDS) ?? undefined
    : undefined
  const envelopeHasZeroFeasibleHosts = Boolean(competitiveRequest && capacityEnvelope?.dataAvailable && !capacityEnvelopeLoading && !capacityEnvelopeError && capacityEnvelope && feasibleHostCount(capacityEnvelope.hosts, envelopeRequested, envelopeReplicas) === 0)
  const envelopeExcludingAxes = envelopeHasZeroFeasibleHosts && capacityEnvelope ? limitingAxes(capacityEnvelope.hosts, envelopeRequested, envelopeReplicas) : []
  const envelopeResourceError = envelopeHasZeroFeasibleHosts
    ? envelopeExcludingAxes.length > 0
      ? `${envelopeExcludingAxes.map((axis) => axis === 'cpu' ? 'CPU' : axis === 'memory' ? 'Memory' : axis === 'storage' ? 'Storage' : 'GPU').join(', ')} ${envelopeExcludingAxes.length === 1 ? 'excludes' : 'exclude'} every observed provider. Reduce the requested size and try again.`
      : 'No single provider has all requested resources together. Reduce the requested size and try again.'
    : null
  const sizeSourceKey = sizingOffer?.host_offer_id
    ? `${storageKey}:offer:${sizingOffer.host_offer_id}`
    : sizingHost?.observation_state === 'fresh'
      ? `${storageKey}:catalog:${sizingHost.owner}`
      : null
  const restoredStorageKey = useRef<string | null>(null)

  useEffect(() => {
    if (restoredStorageKey.current === storageKey) return
    restoredStorageKey.current = storageKey
    const next = readStoredDraft(walletAddress)
    if (next) {
      setDraft(applyLinkPrecedence(next, { rentalPath, tenantMax }))
      setRestored(true)
    }
  }, [rentalPath, storageKey, tenantMax, walletAddress])

  useEffect(() => {
    if (!sizeConfig || !sizeSourceKey) return

    // A selected provider is an explicit sizing context. A host product is the
    // machine itself, priced on the host (see HOST_PRODUCT_SIZE_NOTE), so its
    // resources follow the offer on every refresh. A custom-capacity purchase
    // is sized by the tenant, so keep the draft's existing resource choice and
    // only clamp it into this provider's bounds. Non-resource fields in the
    // saved draft are still restored normally.
    setDraft((current) => {
      // The host product chose that size, not the tenant, so it does not count
      // as a custom size they set.
      const seeded = rentalMode !== undefined && isHostProduct(rentalMode)
        ? { ...current, ...sizeConfig.starting, sizedAxes: undefined }
        : current
      return clampDraftResources(seeded, sizeConfig.bounds)
    })
  }, [rentalMode, sizeConfig, sizeSourceKey])

  // Where a host's Size card opens, once per wallet and host, after the host
  // has been read: the size a resumed draft holds when it still fits, else
  // the suggested size (D2), and the whole host only when no smaller size can
  // be priced here (D3). A fresh draft never opens on the whole host.
  const sizeInitKey = hostSizing && !hostLoading && (customAvailable || wholeAvailable) ? `${storageKey}|${selectedHost?.owner ?? ''}` : null
  const sizeInitDone = useRef<string | null>(null)
  useEffect(() => {
    if (!sizeInitKey || sizeInitDone.current === sizeInitKey) return
    sizeInitDone.current = sizeInitKey
    setDraft((current) => {
      const held = draftChoice(current)
      if (held === 'whole' && wholeAvailable) return applySizeChoice(current, 'whole', customBounds ?? DEFAULT_SIZE_BOUNDS)
      if (customAvailable && customBounds) {
        if (held === 'custom') return applySizeChoice(current, 'custom', customBounds)
        if (isFixedPreset(held) && presetFits(held, customBounds).fits) return applySizeChoice(current, held, customBounds)
      }
      // Configure chose this size, not the tenant (isUntouchedDraft).
      return { ...applySizeChoice(current, startingChoice, customBounds ?? DEFAULT_SIZE_BOUNDS), sizeAutoChosen: true }
    })
  }, [customAvailable, customBounds, sizeInitKey, startingChoice, wholeAvailable])

  // A refresh can leave a picked size bigger than what is now free. Custom is
  // clamped by the effect above; a preset falls back and says so.
  useEffect(() => {
    if (!hostSizing || !customBounds || !customAvailable || sizeInitDone.current !== sizeInitKey) return
    const held = draft.sizePreset
    if (!isFixedPreset(held) || !matchesPreset(draft, SIZE_PRESETS[held]) || presetFits(held, customBounds).fits) return
    setSizeNotice(`${CARD_LABEL[held]} no longer fits on this host, so the size is now ${startingChoice ? CARD_LABEL[startingChoice] : 'Custom'}.`)
    setDraft((current) => applySizeChoice(current, startingChoice, customBounds))
  }, [customAvailable, customBounds, draft, hostSizing, sizeInitKey, startingChoice])

  // A host product is one host's capacity, priced once (see REPLICAS_ON_HOST_PRODUCT).
  // A draft restored with replicas, or a mode change onto a host product, drops them.
  useEffect(() => {
    if (!hostProduct || draft.recoveryMode !== 'replicated') return
    setDraft((current) => ({ ...current, recoveryMode: 'single', autoRecovery: false }))
  }, [draft.recoveryMode, hostProduct])

  useEffect(() => {
    if (!competitiveRequest || !envelopeBounds || !capacityEnvelope?.dataAvailable) return
    setDraft((current) => clampDraftResources(current, envelopeBounds))
  }, [capacityEnvelope?.dataAvailable, competitiveRequest, envelopeBounds])

  // Which door, host and offer this draft belongs to, so the front door can
  // offer to resume it without guessing (core/draftOrigin). Built from props,
  // never from window.location.
  const originHost = selectedHost?.owner
  const originOffer = selectedOffer?.host_offer_id
  const draftForStorage = (): GuidedDraft => ({
    ...draft,
    // Persist the provider-derived ceiling for the exact resources and replica
    // count above. Clear it on every other path so an old price cannot leak.
    providerPostedPrice: rentalMode === 'RENTAL_MODE_DIRECT_CUSTOM' ? providerPricePerHour : undefined,
  })
  // Returns whether the draft was stored. The debounced save ignores the
  // answer; the submit flush must not, because Review signs what is stored.
  const saveDraft = (): boolean => {
    let saved = true
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(draftForStorage()))
    } catch {
      saved = false
    }
    writeDraftOrigin(walletAddress, { path: effectivePath, host: originHost, offer: originOffer, max: tenantMax })
    return saved
  }
  const saveDraftRef = useRef(saveDraft)
  saveDraftRef.current = saveDraft

  useEffect(() => {
    if (sizeUnavailable) return
    const timer = window.setTimeout(() => saveDraftRef.current(), 400)
    return () => window.clearTimeout(timer)
  }, [sizeUnavailable, storageKey, draft, effectivePath, originHost, originOffer, tenantMax, providerPricePerHour, rentalMode])

  const errors = useMemo(() => {
    const next: Record<string, string> = {}
    if (!draft.name.trim()) next.name = 'Give this machine a name.'
    if (!draft.image.trim()) next.image = 'Enter a container image.'
    const buildError = buildErrors(draft)
    if (buildError) { delete next.image; next.build = buildError }
    if (draft.workloadSource === 'dockerfile' && draft.ports.some((port) => port.container === 18081)) next.build = 'Port 18081 is reserved for build control.'
    if (sizeUnavailable) {
      next.resources = 'This host has no free capacity right now. Choose another host.'
    }
    if (envelopeResourceError) {
      next.resources = envelopeResourceError
    }
    if (!sizeUnavailable) {
      for (const axis of unsizedAxes) next[`size-${axis}`] = `Choose how much ${AXIS_NAME[axis]} to rent.`
    }
    if (rentalMode === 'RENTAL_MODE_DIRECT_CUSTOM' && sized && !providerPricePerHour) {
      next.providerPrice = hostLoading
        ? 'Waiting for this provider’s current rates before preparing a quote.'
        : 'This provider has no usable current rate card for this custom rental. Choose another host.'
    }
    for (const [index, port] of draft.ports.entries()) {
      if (!Number.isInteger(port.container) || port.container < 1 || port.container > 65535) next[`port-${index}`] = 'Enter a port from 1 to 65535.'
      else if (draft.ports.findIndex((other) => other.container === port.container && other.protocol === port.protocol) < index) next[`port-${index}`] = 'This port is already listed.'
      if (port.allowFrom !== undefined) {
        if (port.allowFrom.length === 0) next[`port-access-${index}`] = 'Add at least one address, or choose Anyone.'
        else {
          const list = normalizeAllowFrom(port.allowFrom)
          if (!list.ok) next[`port-access-${index}`] = list.message
        }
      }
    }
    const bodyError = maxBodyError(draft.ports.find(servesHttpIngress)?.maxBodyMiB)
    if (bodyError) next.httpbody = bodyError
    if (draft.volume && volumeUnavailable) {
      // Restored, or applied with a catalog entry, on a rental that cannot carry it.
      next['volume-mount'] = volumeUnavailable
    } else if (draft.volume) {
      const volume = volumeErrors(draft.volume)
      if (volume.mount) next['volume-mount'] = volume.mount
      if (volume.size) next['volume-size'] = volume.size
      // The host checks the instance disk and the volume together against its free disk.
      else if (sizeConfig && totalStorageGi(draft) > sizeConfig.bounds.storage.max) {
        next['volume-size'] = `Storage and the volume together need ${totalStorageGi(draft)} GB, and this host has ${sizeConfig.bounds.storage.max} GB free.`
      }
    }
    const duplicateKeys = duplicateKeyIndexes(draft.env)
    for (const [index, row] of draft.env.entries()) {
      if (row.key.trim() && !/^[A-Z_][A-Z0-9_]*$/i.test(row.key.trim())) next[`env-${index}`] = 'Use letters, numbers, and underscores for the key.'
      // The SDL drops a keyless row, so a value typed without a key would vanish unannounced.
      else if (!row.key.trim() && row.value !== '') next[`env-${index}`] = 'Add a key or remove this row.'
      // Case-sensitive, as environment variables are; only one value would take effect.
      else if (duplicateKeys.has(index)) next[`env-${index}`] = 'This key is already set above. Only one value is used.'
    }
    // An image that needs a value it has no safe default for (PostgreSQL's
    // password) must not be sent without one. A removed row,
    // or an image typed by hand, is flagged on the image field itself.
    for (const { index, key, preset } of missingRequiredEnv(draft)) {
      if (index === null) {
        if (!next.image) next.image = `${preset.name} needs ${key}. Add it under Environment.`
      } else next[`envvalue-${index}`] = `Set a value for ${key}.`
    }
    // A public port the image leaves unencrypted is the tenant's informed
    // choice, never a default: it is acknowledged here, before any signing.
    const plaintext = unacknowledgedPlaintext(draft)
    if (plaintext) next.plaintext = plaintextConfirmError(plaintext.preset.name, plaintext.ports)
    return next
  }, [draft, envelopeResourceError, hostLoading, providerPricePerHour, rentalMode, sizeConfig, sizeUnavailable, sized, unsizedAxes, volumeUnavailable])

  const firstInvalidKey = Object.keys(errors)[0]
  const firstInvalidId = firstInvalidKey ? fieldIdForError(firstInvalidKey) : undefined

  // The checklist reads the same error memo as the fields, but is shown before
  // submit too; the fields themselves still only turn red after submit.
  const envelopeUsable = competitiveRequest && capacityEnvelope?.dataAvailable && !capacityEnvelopeLoading && !capacityEnvelopeError ? capacityEnvelope : null
  const readinessItems = readiness({
    draft,
    errors,
    rentalMode,
    hostBounds: sized ? sizeConfig?.bounds ?? null : null,
    sizeUnavailable,
    envelope: envelopeUsable ? { feasible: feasibleCount(envelopeUsable, envelopeRequested, envelopeReplicas), resolution: envelopeUsable.resolution } : null,
    postedRate,
  })
  // The bound host's published rate card: live pricing for custom capacity,
  // and the working behind a host product's posted rate. Direct-bound only.
  const price = summaryPrice({ draft, rentalMode, openRequest, postedRate, rates: hostRates, offer: sizingOffer, unsized: !sized })
  // A direct rental's next screen is the host's quote, not a review.
  const continueLabel = directRental ? 'Continue to quote' : 'Continue to review'

  const update = <K extends keyof GuidedDraft>(key: K, value: GuidedDraft[K]) => setDraft((current) => ({ ...current, [key]: value }))
  // An image with its own S3 variable names (catalog `s3Env`) gets them written too.
  const s3Preset = popularImageForRepository(draft.image)
  const s3Mapping = s3Preset?.s3Env ? { name: s3Preset.name, env: s3Preset.s3Env } : undefined
  /*
    The mapping is the image's, not the connection's: when the image changes
    after a connection was saved, re-write it (`reapplyS3Mapping`).
  */
  const s3MappingKey = JSON.stringify(s3Mapping?.env ?? null)
  useEffect(() => {
    setDraft((current) => {
      const applied = current.s3Connection ? reapplyS3Mapping(current.env, current.s3Connection, s3Mapping?.env) : null
      return applied ? { ...current, env: applied.env, s3Connection: applied.record } : current
    })
    // Keyed on the mapping's content: `s3Mapping` is a new object every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s3MappingKey])
  const sizeContextState: SizeContextState = hostLoading ? 'loading' : sizeUnavailable ? 'unavailable' : sizeConfig ? 'ready' : 'unreported'
  const sizeLocked = Boolean(rentalMode !== undefined && isHostProduct(rentalMode) && sizeConfig && !sizeUnavailable)
  const lockedNote = sizeLocked && rentalMode ? HOST_PRODUCT_SIZE_NOTE[rentalMode] : undefined
  // The exclusive product is only reached by an old link; it keeps its way out.
  const exclusiveLink = sizeLocked && rentalMode === 'RENTAL_MODE_DIRECT_HOST_EXCLUSIVE'
  const sizeMeters = sizeConfig ? sizeMetersFor(sizeConfig, draft) : null
  const anyMeter = Boolean(sizeMeters && (sizeMeters.cpu || sizeMeters.ram || sizeMeters.storage))
  // Taking everything is the host product, so only custom capacity warns near the limit.
  const nearLimit = sizeConfig && !sizeUnavailable && sized && rentalMode === 'RENTAL_MODE_DIRECT_CUSTOM' ? nearLimitAxes(draft, sizeConfig.bounds) : []
  // Replicated recovery asks one host for every replica (the SDL's `count`),
  // while each control is capped at the host's full free capacity. The form
  // does not re-bound for replicas (a known gap); it must not claim a fit it
  // cannot back either. The provider multiplies every storage entry by
  // `count`, the persistent volume included.
  const replicaOverflow = sizeConfig && !sizeUnavailable && sized && envelopeReplicas > 1
    ? SIZE_AXES.filter((axis) => (axis === 'storage' ? totalStorageGi(draft) : draft[axis]) * envelopeReplicas > sizeConfig.bounds[axis].max)
    : []
  const showFit = Boolean(sizingHost && sizeConfig && !sizeUnavailable && sized)
  const showSummaryLine = hostSizing && sized && !sizeUnavailable && Boolean(sizeConfig)
  const sizeFooter = anyMeter || showFit || showSummaryLine ? <div className="size-footer">
    {showSummaryLine && <p className="size-summary">{summaryLine(draft)} · {sizeLocked ? 'fixed host price' : 'priced by size'}</p>}
    {anyMeter && <ul className="capacity-legend" aria-label="Capacity legend">
      <li><span className="capacity-swatch capacity-swatch-request" aria-hidden="true" />Your instance</li>
      <li><span className="capacity-swatch capacity-swatch-free" aria-hidden="true" />Free</li>
      <li><span className="capacity-swatch capacity-swatch-used" aria-hidden="true" />Used by others</li>
    </ul>}
    {showFit && <span className="size-fit" aria-live="polite">{replicaOverflow.length > 0
      ? <span className="size-fit-caution">{envelopeReplicas} replicas need more {replicaOverflow.map((axis) => AXIS_NAME[axis]).join(' and ')} than this host has free.</span>
      : nearLimit.length > 0
      ? <span className="size-fit-caution">Close to this host's free {nearLimit.map((axis) => AXIS_NAME[axis]).join(' and ')}. It can change before the host signs a quote.</span>
      : <Badge tone="positive">Fits on this host</Badge>}</span>}
  </div> : undefined

  const selectSize = (id: SizePresetId) => {
    setSizeNotice(null)
    setDraft((current) => ({ ...applySizeChoice(current, id, id === 'whole' ? sizeBounds : customBounds ?? sizeBounds), sizeAutoChosen: undefined }))
  }
  // Any value the tenant sets by hand makes the size theirs: Custom.
  const setSize = (axis: SizeAxis, value: number) => {
    setSizeNotice(null)
    setDraft((current) => ({ ...current, [axis]: value, sizePreset: 'custom', sizeAutoChosen: undefined, sizedAxes: SIZE_AXES.filter((known) => known === axis || current.sizedAxes?.includes(known)) }))
  }

  // The cards. On a host, prices come from the host's own rates and posted
  // price (decision D4); elsewhere there is no host to price against.
  const hourly = (amount: bigint | string | null | undefined) => amount === null || amount === undefined || amount === '' || amount === 0n ? null : formatUsdPerHour({ denom: 'uddt', amount: amount.toString() }, BLOCK_SECONDS)
  const fixedPrice = (size: SizeValues) => hostRates && customAvailable ? hourly(priceForDraft(hostRates, size, envelopeReplicas)?.totalPerBlock) : null
  // Only a host's card opens on a suggestion; without a host there is none to mark.
  const suggestedId: SizePresetId | null = hostSizing ? startingChoice : null
  const fixedCards: SizeCardModel[] = FIXED_PRESET_IDS.map((id: FixedPresetId) => ({
    id,
    label: CARD_LABEL[id],
    spec: SIZE_PRESETS[id],
    price: hostSizing ? fixedPrice(SIZE_PRESETS[id]) : null,
    unavailable: customUnavailable ?? (hostSizing && customBounds ? shortfallText(id, customBounds) : !hostSizing && !presetFits(id, sizeBounds).fits ? 'More than is free.' : null),
    suggested: id === suggestedId,
  }))
  const wholeSpec = wholeConfig ? { cpu: wholeConfig.starting.cpu, ram: wholeConfig.starting.ram, storage: wholeConfig.starting.storage } : null
  const heldChoice = draftChoice(draft)
  const customSelected = !sizeLocked && (heldChoice === 'custom' || (!hostSizing && heldChoice === undefined && derivePreset(draft) === 'custom'))
  const sizeCards: SizeCardModel[] = [
    ...fixedCards,
    ...(hostSizing ? [{ id: 'whole' as const, label: CARD_LABEL.whole, spec: wholeSpec, price: wholeAvailable ? hourly(postedRate) : null, unavailable: wholeUnavailable }] : []),
    { id: 'custom', label: CARD_LABEL.custom, spec: customSelected && sized ? draft : null, price: customSelected && sized && hostSizing ? fixedPrice(draft) : null, unavailable: customUnavailable },
  ]
  const selectedSize: SizePresetId | null = sizeLocked
    ? 'whole'
    : !sized ? null
    : heldChoice && heldChoice !== 'whole' ? heldChoice : derivePreset(draft)
  const sizeError = (axis: SizeAxis) => submitted ? errors[`size-${axis}`] : undefined

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitted(true)
    if (Object.keys(errors).length > 0) {
      // A collapsed capacity bound disables the resource slider. Do not move
      // focus to that disabled control; the validation summary still provides
      // the message and jump link for a user who wants to inspect the Size card.
      window.setTimeout(() => {
        if (firstInvalidKey !== 'resources' && firstInvalidId) document.getElementById(firstInvalidId)?.focus()
      }, 0)
      return
    }
    // Flush the debounce before navigating so Review signs exactly what the user saw.
    // Review rebuilds the SDL from storage, so a failed write would sign an
    // older draft; stop here instead.
    if (!saveDraft()) {
      setSaveError(true)
      return
    }
    setSaveError(false)

    // A direct rental reviews a signed provider quote, which the competitive
    // review screen has nothing to show. `rentalMode` is used rather than
    // `rentalPath` so that a tenant who typed a maximum here is sent to the
    // competitive flow they are now actually in — the same
    // derivation-beats-selection rule the chooser announced.
    if (directRental && effectivePath) {
      const params = new URLSearchParams({ path: effectivePath })
      if (selectedHost?.owner) params.set('host', selectedHost.owner)
      if (selectedOffer?.host_offer_id && rentalMode !== undefined && isHostProduct(rentalMode)) params.set('offer', selectedOffer.host_offer_id)
      onNavigate?.(`/app/rent/direct?${params.toString()}`)
      return
    }

    onNavigate?.('/app/rent/review')
  }

  const editAsSdl = () => {
    try {
      const persisted = draftForStorage()
      const sdl = rentalMode === 'RENTAL_MODE_DIRECT_CUSTOM'
        ? rentalDraftToSdl(persisted)
        : draft.priceCap.trim() === '' ? guidedDraftToSdl(persisted) : rentalDraftToSdl(persisted)
      window.localStorage.setItem(`ddws.console.sdl.prefill.${walletAddress ?? 'anonymous'}`, sdl)
      window.localStorage.setItem(`ddws.console.sdl.prefill.${walletAddress ?? 'anonymous'}.build-input`, JSON.stringify(persisted.buildInput ?? null))
      onNavigate?.('/app/advanced/sdl')
    } catch (cause) {
      setSdlError(cause instanceof Error ? cause.message : 'The guided configuration could not be converted to SDL yet.')
    }
  }

  return <>
    <PageHeader back={{ label: selectedHost ? 'Back to hosts' : 'Back', onClick: () => onNavigate?.(selectedHost ? '/app/rent/hosts' : '/app/rent') }} eyebrow="Rent" title="Configure your machine" description="Choose resources without writing YAML. Review the generated configuration before signing." actions={<Button variant="ghost" onClick={() => { setSdlError(null); setSdlConfirmOpen(true) }}>Edit as SDL</Button>} />
    {restored && <div className="notice notice-info" role="status">Restored your draft.{' '}
      {linkCap !== ''
        ? `The maximum you just entered ($${linkCap} per hour) is used instead of the one saved in the draft.`
        : 'Anything carried in from the previous screen is used instead of the saved draft.'}{' '}
      <Button variant="ghost" onClick={() => setRestored(false)}>Dismiss</Button></div>}
    {chosenPath && rentalMode ? <RentalChoiceStrip rentalMode={rentalMode} openRequest={openRequest} chosenPath={chosenPath} onNavigate={onNavigate} /> : null}
    {saveError && <div className="notice notice-caution" role="alert"><strong>Your browser could not save this configuration.</strong><p>It cannot be reviewed until it is saved. Free some site storage and try again.</p></div>}
    {submitted && firstInvalidKey && firstInvalidId && <div className="notice notice-caution" id="configure-errors" role="alert"><strong>Review the highlighted fields.</strong><p>Fix the first issue below before continuing.</p><a href={`#${firstInvalidId}`}>Jump to the first issue: {errors[firstInvalidKey]}</a></div>}
    <div className="configure-layout">
      <form className="form-stack" id="configure-form" aria-describedby={submitted && firstInvalidId ? 'configure-errors' : undefined} onSubmit={submit} noValidate>
        <BuildSourceCard draft={draft} setDraft={setDraft} error={submitted ? errors.build : undefined} />
        {draft.workloadSource !== 'dockerfile' && <WorkloadCard draft={draft} setDraft={setDraft} errors={errors} submitted={submitted} rentalMode={rentalMode} sizeBounds={sizeBounds} onNavigate={onNavigate} />}

        <Card title="How big should this instance be?" id="card-size" className="size-card" action={selectedHost && hostSizing ? <SizeContext host={selectedHost} state={hostLoading ? 'loading' : 'ready'} /> : sizingHost && (sizeContextState === 'ready' || sizeContextState === 'loading') ? <SizeContext host={sizingHost} state={sizeContextState} /> : undefined} footer={sizeFooter}>
          {sizingHost && (sizeContextState === 'unavailable' || sizeContextState === 'unreported') && !(hostSizing && (customAvailable || wholeAvailable)) && <SizeContext host={sizingHost} state={sizeContextState} />}
          {competitiveRequest && <SizeEnvelope envelope={capacityEnvelope} requested={envelopeRequested} replicas={envelopeReplicas} isLoading={capacityEnvelopeLoading} error={capacityEnvelopeError} />}
          {exclusiveLink && lockedNote && sizingHost && <div className="size-locked" role="note">
            <p>{lockedNote}</p>
            <Button type="button" variant="link" onClick={() => onNavigate?.(customCapacityHref(sizingHost.owner))}>Choose the size yourself instead</Button>
          </div>}
          {!exclusiveLink && <Suspense fallback={null}><SizePicker cards={sizeCards} selected={selectedSize} disabled={sizeUnavailable && !hostSizing} onSelect={selectSize} /></Suspense>}
          <div className="resource-rows">
            <ResourceControl id="cpu" label="CPU" value={draft.cpu} bound={sizeBounds.cpu} meter={sizeMeters?.cpu} capIsFree={Boolean(sizeConfig)} disabled={sizeUnavailable} locked={sizeLocked} unset={unsizedAxes.includes('cpu')} error={sizeError('cpu')} onChange={(value) => setSize('cpu', value)} />
            <ResourceControl id="ram" label="Memory" value={draft.ram} bound={sizeBounds.ram} meter={sizeMeters?.ram} capIsFree={Boolean(sizeConfig)} disabled={sizeUnavailable} locked={sizeLocked} unset={unsizedAxes.includes('ram')} error={sizeError('ram')} onChange={(value) => setSize('ram', value)} />
            <ResourceControl id="storage" label="Storage" value={draft.storage} bound={sizeBounds.storage} meter={sizeMeters?.storage} capIsFree={Boolean(sizeConfig)} disabled={sizeUnavailable} locked={sizeLocked} unset={unsizedAxes.includes('storage')} error={sizeError('storage')} onChange={(value) => setSize('storage', value)} />
          </div>
          {hostSizing && !sizeUnavailable && (sizeLocked
            ? <p className="size-note size-note-locked" role="note"><Lock aria-hidden="true" size={14} />{HOST_PRODUCT_SIZE_NOTE.RENTAL_MODE_DIRECT_HOST_AVAILABLE}</p>
            : <p className="size-note" role="note">{SIZED_NOTE}</p>)}
          {sizeNotice && <p className="size-note size-note-caution" role="status">{sizeNotice}</p>}
        </Card>

        <Card title="Persistent volume" id="card-volume">
          <VolumeSection volume={draft.volume} unavailable={volumeUnavailable} remedy={hostProduct && hostSizing ? 'Pick a size other than Whole host above to add one.' : undefined} onChooseCustom={hostProduct && !hostSizing && sizingHost && onNavigate ? () => onNavigate(customCapacityHref(sizingHost.owner)) : undefined} errors={errors} submitted={submitted} onChange={(volume) => update('volume', volume)} />
        </Card>

        <Card title="Networking" id="card-networking" action={<span className="card-count">{portCountLabel(draft.ports.length, restrictedPorts(draft.ports).length)}</span>}>
          <PortList ports={draft.ports} errors={errors} submitted={submitted} myIp={myIp} onChange={(ports) => update('ports', ports)} />
        </Card>

        <Card title="Environment" action={<EnvPasteButton open={envPasteOpen} onOpen={() => setEnvPasteOpen(true)} />}>
          <EnvList env={draft.env} errors={errors} submitted={submitted} pasteOpen={envPasteOpen} onPasteOpenChange={setEnvPasteOpen} maskedKeys={draft.s3Connection?.keys} onChange={(env) => update('env', env)} />
        </Card>

        <Card title="S3 connection" id="card-s3">
          <S3Connection env={draft.env} record={draft.s3Connection} instances={s3Instances} mapping={s3Mapping} resolveEndpoint={resolveS3Endpoint} onChange={(env, s3Connection) => setDraft((current) => ({ ...current, env, s3Connection }))} />
        </Card>

        <Card title="Availability">
          <Suspense fallback={<p className="field-help">Loading availability options…</p>}><AvailabilityOptions mode={draft.recoveryMode} replicaCount={draft.replicaCount} replicatedUnavailable={hostProduct ? REPLICAS_ON_HOST_PRODUCT : undefined} onModeChange={(recoveryMode) => setDraft((current) => ({ ...current, recoveryMode, autoRecovery: recoveryMode !== 'single' }))} onReplicaChange={(replicaCount) => update('replicaCount', replicaCount)} /></Suspense>
        </Card>

        <Button type="submit" variant="secondary">{continueLabel}</Button>
      </form>
      <ConfigSummary draft={draft} rentalMode={rentalMode} sizingHost={sizingHost} price={price} readiness={readinessItems} submitted={submitted} continueLabel={continueLabel} sizeLocked={sizeLocked} />
    </div>
    {/* Outside the layout grid: a sticky box cannot rise above its parent, and
        the layout starts below the fold's header rows on a phone. */}
    <SummaryBar price={price} readiness={readinessItems} submitted={submitted} continueLabel={continueLabel} />
    <Dialog open={sdlConfirmOpen} onOpenChange={setSdlConfirmOpen}><DialogContent title="Open the SDL editor?" description="The editor is an advanced view of this configuration. Your guided draft will be converted and carried over; changes there will not update this form automatically.">{sdlError && <div className="notice notice-caution" role="alert">{sdlError}</div>}<div className="sign-sheet-actions"><Button onClick={() => setSdlConfirmOpen(false)}>Keep guided form</Button><Button variant="primary" onClick={editAsSdl}>Open SDL editor</Button></div></DialogContent></Dialog>
  </>
}
